import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Check } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailDeliveryAvailable, setEmailDeliveryAvailable] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const response = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setEmailDeliveryAvailable(response.data?.email_delivery !== "unavailable");
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send reset email");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
      <div className="w-full max-w-md">
        <Link to="/signin" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300">
          <ArrowLeft size={14}/> Back to sign in
        </Link>

        {!sent ? (
          <>
            <div className="mt-6">
              <div className="label-eyebrow">Reset</div>
              <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Forgot password?</h1>
              <p className="text-sm text-zinc-400 mt-2">Enter your account email and we’ll request a secure reset link.</p>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
                <input
                  data-testid="forgot-email"
                  type="email" autoComplete="email" required
                  autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
                />
              </div>
              <button
                data-testid="forgot-submit"
                type="submit" disabled={busy}
                className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <div className="mt-6">
            <div className="size-16 rounded-2xl bg-[#34C759]/15 grid place-items-center">
              <Check size={28} className="text-[#34C759]"/>
            </div>
            <h1 className="font-display font-black text-3xl tracking-tighter mt-5">Check your email</h1>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
              {emailDeliveryAvailable ? (
                <>If <span className="text-zinc-200 font-semibold">{email}</span> matches a password account, Proximity will send a reset link to that inbox. Delivery can take a minute; the link expires in 1 hour.</>
              ) : (
                <>Password-reset email is not configured for this Proximity deployment. Please contact the app administrator to enable email delivery.</>
              )}
            </p>
            <div className="mt-6 text-xs text-zinc-500 leading-relaxed">
              {emailDeliveryAvailable ? "Check spam first. If it does not arrive, try again after a minute or contact the app administrator." : "No reset email was requested because delivery is not configured."}
            </div>
            <Link
              to="/signin"
              className="mt-6 inline-block text-sm text-[#007AFF] font-bold"
            >
              Back to sign in →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
